import type { BillingPeriod, BillingUsageRecord, BillingUsageSnapshot } from '@callhub/shared';

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value < 10 ? 2 : 0,
  }).format(value);
}

export function formatBillingAmount(value: number | null, currency: string | null): string {
  if (value === null || !Number.isFinite(value)) return 'N/A';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatBillingUsage(record: {
  usage: number | null;
  usageUnit: string | null;
  count: number | null;
  countUnit: string | null;
}): string {
  if (record.usage !== null && Number.isFinite(record.usage)) {
    return `${formatCompactNumber(record.usage)} ${record.usageUnit ?? ''}`.trim();
  }
  if (record.count !== null && Number.isFinite(record.count)) {
    return `${formatCompactNumber(record.count)} ${record.countUnit ?? ''}`.trim();
  }
  return 'N/A';
}

export function labelForBillingPeriod(period: BillingPeriod): string {
  if (period === 'week') return 'Last 7 Days';
  if (period === 'month') return 'Last 30 Days';
  return 'Last Day';
}

export function formatBillingDate(value: string): string {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

export type BillingServiceRollup = {
  key: string;
  provider: string;
  service: string;
  records: number;
  totalCost: number | null;
  currency: string | null;
};

export type TwilioCategoryRollup = {
  key: string;
  category: string;
  services: number;
  records: number;
  totalCost: number | null;
  currency: string | null;
};

function formatIsoTimestampForFilename(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'snapshot';
  return parsed
    .toISOString()
    .replaceAll(':', '-')
    .replace(/\.\d{3}Z$/, 'Z');
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

function toFiniteNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function buildBillingExportFilename(
  period: BillingPeriod,
  generatedAt: string,
  extension: 'csv' | 'json',
): string {
  return `callhub-billing-${period}-${formatIsoTimestampForFilename(generatedAt)}.${extension}`;
}

export function buildBillingCsv(snapshot: BillingUsageSnapshot): string {
  const headers = [
    'bucket',
    'sourceDate',
    'provider',
    'service',
    'category',
    'description',
    'usage',
    'usageUnit',
    'count',
    'countUnit',
    'cost',
    'currency',
    'source',
  ];

  const rows = snapshot.records.map((record) =>
    [
      record.bucket,
      record.sourceDate,
      record.provider,
      record.service,
      record.category,
      record.description ?? '',
      record.usage === null ? '' : String(record.usage),
      record.usageUnit ?? '',
      record.count === null ? '' : String(record.count),
      record.countUnit ?? '',
      record.cost === null ? '' : String(record.cost),
      record.currency ?? '',
      record.source,
    ]
      .map(csvEscape)
      .join(','),
  );

  return [headers.join(','), ...rows].join('\n');
}

export function buildBillingJson(snapshot: BillingUsageSnapshot): string {
  return JSON.stringify(snapshot, null, 2);
}

export function rollupBillingRecords(records: BillingUsageRecord[]): BillingServiceRollup[] {
  const byService = new Map<string, BillingServiceRollup>();

  for (const record of records) {
    const key = `${record.provider}:${record.service}:${record.currency ?? 'none'}`;
    const current = byService.get(key);
    const numericCost = toFiniteNumber(record.cost);

    if (!current) {
      byService.set(key, {
        key,
        provider: record.provider,
        service: record.service,
        records: 1,
        totalCost: numericCost,
        currency: record.currency ?? null,
      });
      continue;
    }

    current.records += 1;
    if (numericCost !== null) {
      current.totalCost = (current.totalCost ?? 0) + numericCost;
    }
  }

  return [...byService.values()].sort((left, right) => {
    const leftCost = left.totalCost ?? -1;
    const rightCost = right.totalCost ?? -1;
    if (rightCost !== leftCost) return rightCost - leftCost;
    return left.service.localeCompare(right.service);
  });
}

type TwilioCategoryAccumulator = {
  category: string;
  records: number;
  totalCost: number | null;
  currency: string | null;
  services: Set<string>;
};

export function rollupTwilioCategoryRecords(records: BillingUsageRecord[]): TwilioCategoryRollup[] {
  const byCategory = new Map<string, TwilioCategoryAccumulator>();

  for (const record of records) {
    if (record.provider !== 'twilio') continue;

    const key = `${record.category}:${record.currency ?? 'none'}`;
    const current = byCategory.get(key);
    const numericCost = toFiniteNumber(record.cost);
    if (!current) {
      byCategory.set(key, {
        category: record.category,
        records: 1,
        totalCost: numericCost,
        currency: record.currency ?? null,
        services: new Set([record.service]),
      });
      continue;
    }

    current.records += 1;
    current.services.add(record.service);
    if (numericCost !== null) {
      current.totalCost = (current.totalCost ?? 0) + numericCost;
    }
  }

  return [...byCategory.entries()]
    .map(([key, value]) => ({
      key,
      category: value.category,
      services: value.services.size,
      records: value.records,
      totalCost: value.totalCost,
      currency: value.currency,
    }))
    .sort((left, right) => {
      const leftCost = left.totalCost ?? -1;
      const rightCost = right.totalCost ?? -1;
      if (rightCost !== leftCost) return rightCost - leftCost;
      return left.category.localeCompare(right.category);
    });
}
